import { PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { ErrorResponse } from '.';
import { isDevEnv, isTestEnv } from '../config/config';
import { HttpStatusCode } from './thunkActionTypes';

const DEV_QUERY_PARAM = 'x-ms-routing-name=staging-dev';
const TEST_QUERY_PARAM = 'x-ms-routing-name=staging-test';

let globalLogoutCallback: (() => Promise<void> | void) | null = null;

export const setGlobalLogoutCallbackForHelpers = (
  callback: () => Promise<void> | void
) => {
  globalLogoutCallback = callback;
};

export const getErrorResponse = (error: unknown): ErrorResponse => {
  const isAxiosError = (err: unknown): err is AxiosError => {
    return typeof err === 'object' && err !== null && 'response' in err;
  };

  const errorResponse =
    isAxiosError(error) && error.response
      ? error.response
      : {
          status: HttpStatusCode.network_error,
          statusText: 'Network Error',
          data: isAxiosError(error) ? error.message : 'Unknown error',
        };

  if (errorResponse.status === HttpStatusCode.not_authorized) {
    triggerGlobalLogout('401 error');
  }

  return errorResponse;
};

// Helper function to trigger logout with proper error handling
const triggerGlobalLogout = (reason: string) => {
  if (globalLogoutCallback) {
    // Handle both sync and async callbacks safely
    try {
      globalLogoutCallback();
    } catch (logoutError) {
      console.error(
        `Error during automatic logout from helpers (${reason}):`,
        logoutError
      );
    }
  } else {
    console.error(
      `${reason} detected but no global logout callback set in helpers`
    );
  }
};

export const addEnvQueryParam = (url: string) => {
  if (isDevEnv()) {
    if (url.includes('?')) {
      return `${url}&${DEV_QUERY_PARAM}`;
    } else {
      return `${url}?${DEV_QUERY_PARAM}`;
    }
  }

  if (isTestEnv()) {
    if (url.includes('?')) {
      return `${url}&${TEST_QUERY_PARAM}`;
    } else {
      return `${url}?${TEST_QUERY_PARAM}`;
    }
  }

  return url;
};

export const offensiveTextDetected = (
  action: PayloadAction<ErrorResponse | undefined>
) => {
  return (
    action.payload?.status === HttpStatusCode.not_acceptable &&
    action.payload?.data?.['error_сode'] === 'OFFENSIVE_TEXT'
  );
};
